import React, { useState, useEffect } from 'react';
import { Button, Card, Row, Col, Container, Spinner } from 'react-bootstrap';
import { useParams, useHistory } from 'react-router-dom';
import { Formik, Form as FormikForm, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { default as Selector } from 'react-select';
import {
  FaBuilding,
  FaExchangeAlt,
  FaInfoCircle,
  FaSave,
} from 'react-icons/fa';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import {
  BuscarEmpreendimentosPDV,
  buscarParametrosIntegracao,
  salvarParametrosIntegracao,
} from '~/Services/Service';

const ParametrosIntegracao = () => {
  const { id: empreendimentoId } = useParams();
  const history = useHistory();
  const [empreendimentos, setEmpreendimentos] = useState([]);
  const [integracaoData, setIntegracaoData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const carregarEmpreendimentos = async () => {
      try {
        const dados = await BuscarEmpreendimentosPDV();
        setEmpreendimentos(dados || []);
      } catch (error) {
        console.error('Erro ao carregar empreendimentos:', error);
      }
    };
    carregarEmpreendimentos();
  }, []);

  const fetchIntegracaoData = async () => {
    if (!empreendimentoId) {
      setIntegracaoData(null);
      return;
    }
    try {
      setLoading(true);
      const data = await buscarParametrosIntegracao(empreendimentoId);
      setIntegracaoData(data || null);
    } catch (error) {
      console.error('Erro ao buscar parâmetros de integração:', error);
      setIntegracaoData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIntegracaoData();
  }, [empreendimentoId]);

  const optionsEmpreendimentos = empreendimentos.map(item => ({
    value: item.codigo,
    label: item.codigo + ' - ' + item.descricao,
  }));

  const empreendimentoSelecionado = optionsEmpreendimentos.find(
    item => String(item.value) === String(empreendimentoId)
  );

  const initialValues = {
    codigoFamilia:
      integracaoData && integracaoData.codigo_familia != null
        ? integracaoData.codigo_familia
        : empreendimentoId || '',
    idCrm:
      integracaoData && integracaoData.id_crm != null
        ? integracaoData.id_crm
        : '',
    nomeFamilia:
      integracaoData && integracaoData.nome_familia
        ? integracaoData.nome_familia
        : '',
    nomeCrm:
      integracaoData && integracaoData.nome_crm ? integracaoData.nome_crm : '',
  };

  const validationSchema = Yup.object().shape({
    nomeCrm: Yup.string()
      .nullable()
      .max(50, 'Máximo de 50 caracteres'),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const payload = {
        codigo_familia: Number(empreendimentoId),
        nome_crm: values.nomeCrm ? values.nomeCrm : null,
      };

      await salvarParametrosIntegracao(payload);
      toast.success('Dados de integração salvos com sucesso!');
      await fetchIntegracaoData();
    } catch (err) {
      const mensagem =
        err && err.message ? err.message : 'Erro ao salvar os dados.';
      toast.error(mensagem);
    } finally {
      setSubmitting(false);
    }
  };

  const readOnlyFieldStyle = {
    backgroundColor: '#f4f6fa',
    color: '#495057',
    border: '1px solid #e3e6ef',
  };

  return (
    <div>
      <Container fluid className="px-3 py-3">
        <Row
          className="py-3 mb-3 mx-auto justify-content-between align-items-center"
          style={{
            backgroundColor: '#0747a6',
            borderRadius: '8px',
            color: '#fff',
          }}
        >
          <Col className="d-flex align-items-center justify-content-between px-3">
            <h4 className="mb-0">Parâmetros de Integração</h4>
          </Col>
          <Col xs={12} md={6} lg={5}>
            <Selector
              inputId="selector-emp-integracao"
              styles={customStyles}
              components={{ DropdownIndicator }}
              name="empreendimento"
              isSearchable={true}
              isClearable={false}
              options={optionsEmpreendimentos}
              value={empreendimentoSelecionado || null}
              onChange={selected => {
                if (selected) {
                  history.push(
                    `/parametrospdv/integracao/${selected.value}`
                  );
                }
              }}
              placeholder="Selecione..."
            />
          </Col>
        </Row>

        {!empreendimentoId && (
          <Row className="px-3 my-3">
            <Col xs={12} lg={8} className="mx-auto">
              <Card
                className="border-0"
                style={{
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                }}
              >
                <Card.Body className="text-center py-5">
                  <FaInfoCircle
                    size={48}
                    className="mb-3"
                    style={{ color: '#0747a6', opacity: 0.7 }}
                  />
                  <h5 className="mb-2" style={{ color: '#0747a6' }}>
                    Nenhum empreendimento selecionado
                  </h5>
                  <p className="text-muted mb-0">
                    Selecione um empreendimento no campo acima para visualizar e
                    editar os parâmetros de integração.
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {empreendimentoId && loading && (
          <Row className="px-3 my-5">
            <Col className="text-center">
              <Spinner animation="border" variant="primary" />
              <p className="text-muted mt-2 mb-0">Carregando parâmetros...</p>
            </Col>
          </Row>
        )}

        {empreendimentoId && !loading && (
          <Formik
            enableReinitialize={true}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, dirty }) => (
              <FormikForm>
                <Row className="px-3">
                  <Col xs={12} lg={8} className="mx-auto">
                    <Card
                      className="border-0 mb-3"
                      style={{
                        borderRadius: '12px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                      }}
                    >
                      <Card.Header
                        className="bg-white border-bottom py-3"
                        style={{
                          borderRadius: '12px 12px 0 0',
                          borderBottomColor: '#e9ecef',
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <div
                            className="d-flex align-items-center justify-content-center mr-3"
                            style={{
                              width: '36px',
                              height: '36px',
                              borderRadius: '8px',
                              backgroundColor: '#e7f0ff',
                              color: '#0747a6',
                            }}
                          >
                            <FaBuilding />
                          </div>
                          <h6 className="mb-0" style={{ fontWeight: 600 }}>
                            Informações do Empreendimento
                          </h6>
                        </div>
                      </Card.Header>
                      <Card.Body className="py-3">
                        <Row>
                          <Col xs={12} md={4}>
                            <div className="form-group mb-3">
                              <label
                                className="small mb-1 text-muted"
                                style={{ fontWeight: 500 }}
                              >
                                Código Família
                              </label>
                              <Field
                                name="codigoFamilia"
                                type="text"
                                className="form-control form-control-sm"
                                disabled
                                style={readOnlyFieldStyle}
                              />
                            </div>
                          </Col>
                          <Col xs={12} md={4}>
                            <div className="form-group mb-3">
                              <label
                                className="small mb-1 text-muted"
                                style={{ fontWeight: 500 }}
                              >
                                ID CRM
                              </label>
                              <Field
                                name="idCrm"
                                type="text"
                                className="form-control form-control-sm"
                                disabled
                                placeholder="—"
                                style={readOnlyFieldStyle}
                              />
                            </div>
                          </Col>
                          <Col xs={12} md={4}>
                            <div className="form-group mb-3">
                              <label
                                className="small mb-1 text-muted"
                                style={{ fontWeight: 500 }}
                              >
                                Nome da Família
                              </label>
                              <Field
                                name="nomeFamilia"
                                type="text"
                                className="form-control form-control-sm"
                                disabled
                                placeholder="—"
                                style={readOnlyFieldStyle}
                              />
                            </div>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>

                    <Card
                      className="border-0 mb-3"
                      style={{
                        borderRadius: '12px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                      }}
                    >
                      <Card.Header
                        className="bg-white border-bottom py-3"
                        style={{
                          borderRadius: '12px 12px 0 0',
                          borderBottomColor: '#e9ecef',
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <div
                            className="d-flex align-items-center justify-content-center mr-3"
                            style={{
                              width: '36px',
                              height: '36px',
                              borderRadius: '8px',
                              backgroundColor: '#e6faf0',
                              color: '#1e8a5a',
                            }}
                          >
                            <FaExchangeAlt />
                          </div>
                          <h6 className="mb-0" style={{ fontWeight: 600 }}>
                            Configuração do CRM
                          </h6>
                        </div>
                      </Card.Header>
                      <Card.Body className="py-3">
                        <Row>
                          <Col xs={12} md={8}>
                            <div className="form-group mb-2">
                              <label
                                className="small mb-1"
                                style={{ fontWeight: 500 }}
                              >
                                Nome da Campanha
                              </label>
                              <Field
                                name="nomeCrm"
                                type="text"
                                className="form-control form-control-sm"
                                maxLength={50}
                                placeholder="Informe o nome usado no CRM"
                              />
                              <ErrorMessage
                                name="nomeCrm"
                                component="div"
                                className="text-danger small mt-1"
                              />
                              <small className="text-muted">
                                Máximo de 50 caracteres.
                              </small>
                            </div>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>

                    <div className="d-flex justify-content-end align-items-center mb-4">
                      {dirty && (
                        <small className="text-muted mr-3">
                          Alterações não salvas
                        </small>
                      )}
                      <Button
                        type="submit"
                        variant="primary"
                        disabled={isSubmitting || !dirty}
                        className="d-flex align-items-center"
                        style={{
                          borderRadius: '8px',
                          padding: '0.45rem 1.25rem',
                          fontWeight: 500,
                        }}
                      >
                        <FaSave className="mr-2" />
                        {isSubmitting ? 'Salvando...' : 'Salvar alterações'}
                      </Button>
                    </div>
                  </Col>
                </Row>
              </FormikForm>
            )}
          </Formik>
        )}
      </Container>
    </div>
  );
};

export default ParametrosIntegracao;
